import { Fragment, PureComponent } from 'react';
import { Provider } from 'react-redux';
import { loadUser, OidcProvider } from 'redux-oidc';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createStore } from 'shared/state/store';
import { State as SiteState } from 'shared/state/ducks/sites';
import { CurrentSite } from 'client/types';
import { getUserManager, initUserManager } from 'client/util/userManager';
import {
  setPostLoginPage,
  setPostLoginUrl,
} from 'shared/util/locationPersistence';
import SiteRoot from './SiteRoot';
import { Toolkit } from '@uhg-harmony/uitk-react';
import { optumUHCSansFontsStyles } from 'shared/util/fontsStyles';

import { createGlobalStyle, ThemeProvider } from 'styled-components';
import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material';
import { muiTheme } from 'shared/muiThemeOverrides';
import { persistData } from 'shared/util/analyticsPersistence';
import track from 'react-tracking';
import { resolveLegacyThemeFromSite } from 'builder/util/resolveTheme';
import { Theme as SharedTheme } from 'shared/theme/theme';
import { applyPaletteModeToLegacyTheme } from 'shared/util/applyPaletteModeToTheme';
import { getPaletteModeFromSiteTheme } from 'shared/util/themePreference';

interface SiteAppProps {
  site: CurrentSite;
}

interface SiteAppState {
  store?: any;
  paletteMode?: 'dark' | 'light';
}

const GlobalFontStyles = createGlobalStyle`
 ${optumUHCSansFontsStyles};
`;

const publishedThemeFallback = resolveLegacyThemeFromSite({
  enabledTheme: 'dark',
}) as SharedTheme;

export class SiteApp extends PureComponent<SiteAppProps, SiteAppState> {
  private store: any;

  // Published sites intentionally ignore shared user theme preferences; palette is site-config driven.
  private syncPaletteFromSiteTheme = (): void => {
    try {
      const siteMode = this.props.site?.theme
        ? getPaletteModeFromSiteTheme(this.props.site.theme)
        : 'dark';
      this.setState({ paletteMode: siteMode });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('SiteApp: Failed to sync palette from site theme', err);
      }
      // swallow; non-critical for rendering
    }
  };

  constructor(props: SiteAppProps) {
    super(props);
    const publishedSite = {
      ...props.site,
      theme: props.site?.theme || publishedThemeFallback,
    };

    /**
     * Transform the injected @type {CurrentSite} to a @type {SiteState} structure
     */
    const initialState: { sites: SiteState } = {
      sites: {
        meta: {},
        publishMeta: {},
        saveSiteMeta: {},
        data: [
          {
            ...publishedSite,
            pages: publishedSite.pages.reduce((acc, version) => {
              acc[version.pageId] = {
                id: version.pageId,
                current: version,
                versions: [],
              };
              return acc;
            }, {} as { [index: number]: Page }),
            meta: {},
            homepageId: publishedSite.horizonhomepageId,
            postLoginPageId: publishedSite.horizonpostLoginPageId,
          },
        ],
        activeWidgetId: 'root',
        createSiteMeta: {},
        newSiteId: undefined,
        hasPendingDefaultWidgetFetches: false,
      },
    };
    const store = createStore({ ...initialState, published: true });
    this.store = store;
    // Initialize local palette state from the published site theme.
    try {
      const initialSiteMode = getPaletteModeFromSiteTheme(publishedSite.theme);
      this.state = {
        paletteMode: initialSiteMode ?? 'dark',
      };
    } catch (err) {
      this.state = { paletteMode: 'dark' };
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('SiteApp: Failed to initialize palette mode', err);
      }
    }
    publishedSite.postLoginType === 'url'
      ? setPostLoginUrl(publishedSite.postLoginUrl)
      : setPostLoginPage(
          publishedSite.pages.find(p => {
            return p.pageId === publishedSite.horizonpostLoginPageId;
          }) ||
            (publishedSite.pages.length > 0 && publishedSite.pages[0])
        );
    const setPostLogoutPage = (
      postLogoutPage: PostLogoutPage,
      pages: PageVersion[],
      domain: string
    ) => {
      if (postLogoutPage && postLogoutPage.type === 'page') {
        return `https://${domain}/${pages.find(
          p => p.pageId === props.site.horizonhomepageId
        )?.slug || pages[0].slug}`;
      } else if (postLogoutPage && postLogoutPage.type === 'url') {
        return postLogoutPage.url;
      } else {
        return `${window.location.origin}`;
      }
    };

    initUserManager(
      publishedSite.clientId,
      publishedSite.idpHint,
      setPostLogoutPage(
        publishedSite.postLogoutPage,
        publishedSite.pages,
        publishedSite.domain
      )
    );
    loadUser(store, getUserManager());
  }

  componentDidMount(): void {
    try {
      this.syncPaletteFromSiteTheme();
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(
          'SiteApp: Failed to sync palette from site theme in componentDidMount',
          err
        );
      }
      // swallow; non-critical for rendering
    }
  }

  componentDidUpdate(prevProps: SiteAppProps) {
    const prevTheme = prevProps.site?.theme;
    const currTheme = this.props.site?.theme;
    if (currTheme && currTheme !== prevTheme) {
      const mode = getPaletteModeFromSiteTheme(currTheme);
      this.setState({ paletteMode: mode });
    }
  }

  render() {
    const { site } = this.props;
    const siteTheme = site?.theme || publishedThemeFallback;
    const store = this.store;

    const siteMode = getPaletteModeFromSiteTheme(siteTheme);
    const paletteMode = this.state?.paletteMode ?? siteMode ?? 'dark';
    const resolvedTheme = resolveLegacyThemeFromSite(siteTheme) as SharedTheme;
    const overlaidTheme = applyPaletteModeToLegacyTheme(
      resolvedTheme,
      paletteMode
    );

    const fontFamilyOverride =
      resolvedTheme?.font?.family?.base?.value ||
      muiTheme.typography.fontFamily;
    // Maintain legacy observable for tests and any consumers mutating the shared theme
    (muiTheme as any).palette = {
      ...(muiTheme as any).palette,
      mode: paletteMode,
    };
    const appliedMuiTheme = createTheme({
      ...muiTheme,
      typography: { ...muiTheme.typography, fontFamily: fontFamilyOverride },
      palette: { ...(muiTheme as any).palette, mode: paletteMode },
    } as any);

    return (
      <Fragment>
        <ThemeProvider theme={overlaidTheme}>
          <MUIThemeProvider theme={appliedMuiTheme}>
            <CssBaseline />
            <Toolkit appId="LINK_HORIZON_SELF_SERVICE">
              <GlobalFontStyles />
              <Provider store={store}>
                <OidcProvider store={store} userManager={getUserManager()}>
                  <SiteRoot siteId={site.siteId} basename="/" />
                </OidcProvider>
              </Provider>
              <ToastContainer
                position="bottom-right"
                hideProgressBar={true}
                autoClose={3000}
                closeButton={false}
              />
            </Toolkit>
          </MUIThemeProvider>
        </ThemeProvider>
      </Fragment>
    );
  }
}

export default track(
  {},
  {
    dispatch: trackingData => persistData(trackingData),
  }
)(SiteApp);
